import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Suggestion, SuggestionCategory } from "./plugins/SuggestionPlugin";
import styles from "./SuggestionControls.module.css";

export type ConfidenceLevel = "high" | "medium" | "low";
export type SortOption = "default" | "confidence-high" | "confidence-low";

export interface SuggestionControlsProps {
  suggestions: Suggestion[];
  activeFilters: SuggestionCategory[];
  onFilterChange: (filters: SuggestionCategory[]) => void;
  onAcceptAll: (category?: SuggestionCategory) => void;
  onRejectAll: (category?: SuggestionCategory) => void;
  confidenceFilter?: ConfidenceLevel | null;
  onConfidenceFilterChange?: (level: ConfidenceLevel | null) => void;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
}

const CATEGORY_CONFIG: Record<SuggestionCategory, { label: string; description: string }> = {
  grammar: { label: "Grammar", description: "Grammar and mechanics" },
  clarity: { label: "Clarity", description: "Word choice and clarity" },
  structure: { label: "Structure", description: "Flow and organization" },
  style: { label: "Style", description: "Voice and tone" },
  logic: { label: "Logic", description: "Factual and logical issues" },
};

const ALL_CATEGORIES: SuggestionCategory[] = ["grammar", "clarity", "structure", "style", "logic"];

const CONFIDENCE_CONFIG: Record<
  ConfidenceLevel,
  { label: string; minScore: number; maxScore: number }
> = {
  high: { label: "High", minScore: 0.8, maxScore: 1.0 },
  medium: { label: "Medium", minScore: 0.5, maxScore: 0.8 },
  low: { label: "Low", minScore: 0, maxScore: 0.5 },
};

function getConfidenceLevel(score: number): ConfidenceLevel {
  if (score >= 0.8) return "high";
  if (score >= 0.5) return "medium";
  return "low";
}

export function SuggestionControls({
  suggestions,
  activeFilters,
  onFilterChange,
  onAcceptAll,
  onRejectAll,
  confidenceFilter,
  onConfidenceFilterChange,
  sortBy = "default",
  onSortChange,
}: SuggestionControlsProps) {
  const { t } = useTranslation();
  const [expandedBulkAction, setExpandedBulkAction] = useState<"accept" | "reject" | null>(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const categoryCounts = useMemo(() => {
    const counts: Record<SuggestionCategory, number> = {
      grammar: 0,
      clarity: 0,
      structure: 0,
      style: 0,
      logic: 0,
    };
    for (const s of suggestions) {
      counts[s.category]++;
    }
    return counts;
  }, [suggestions]);

  const confidenceCounts = useMemo(() => {
    const counts: Record<ConfidenceLevel, number> = { high: 0, medium: 0, low: 0 };
    for (const s of suggestions) {
      counts[getConfidenceLevel(s.confidenceScore)]++;
    }
    return counts;
  }, [suggestions]);

  const totalCount = suggestions.length;

  const toggleConfidenceFilter = useCallback(
    (level: ConfidenceLevel) => {
      if (!onConfidenceFilterChange) return;
      onConfidenceFilterChange(confidenceFilter === level ? null : level);
    },
    [confidenceFilter, onConfidenceFilterChange],
  );

  const handleSortChange = useCallback(
    (option: SortOption) => {
      onSortChange?.(option);
      setShowSortDropdown(false);
    },
    [onSortChange],
  );

  const toggleFilter = useCallback(
    (category: SuggestionCategory) => {
      if (activeFilters.includes(category)) {
        onFilterChange(activeFilters.filter((f) => f !== category));
      } else {
        onFilterChange([...activeFilters, category]);
      }
    },
    [activeFilters, onFilterChange],
  );

  const toggleAllFilters = useCallback(() => {
    if (activeFilters.length === ALL_CATEGORIES.length) {
      onFilterChange([]);
    } else {
      onFilterChange([...ALL_CATEGORIES]);
    }
  }, [activeFilters, onFilterChange]);

  const handleBulkAction = useCallback(
    (action: "accept" | "reject", category?: SuggestionCategory) => {
      if (action === "accept") {
        onAcceptAll(category);
      } else {
        onRejectAll(category);
      }
      setExpandedBulkAction(null);
    },
    [onAcceptAll, onRejectAll],
  );

  if (totalCount === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <CheckIcon />
          <span>{t("editor.noSuggestions")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          Suggestions
          <span className={styles.totalCount}>{totalCount}</span>
        </h3>
        <div className={styles.bulkActions}>
          <div className={styles.bulkActionWrapper}>
            <button
              type="button"
              className={styles.bulkButton}
              onClick={() =>
                setExpandedBulkAction(expandedBulkAction === "accept" ? null : "accept")
              }
              title="Accept suggestions"
            >
              <AcceptIcon />
              Accept
              <ChevronIcon expanded={expandedBulkAction === "accept"} />
            </button>
            {expandedBulkAction === "accept" && (
              <div className={styles.bulkDropdown}>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => handleBulkAction("accept")}
                >
                  All suggestions ({totalCount})
                </button>
                <div className={styles.dropdownDivider} />
                {ALL_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={styles.dropdownItem}
                    onClick={() => handleBulkAction("accept", cat)}
                    disabled={categoryCounts[cat] === 0}
                  >
                    <span
                      className={styles.categoryDot}
                      style={{ backgroundColor: `var(--color-suggest-${cat})` }}
                    />
                    {CATEGORY_CONFIG[cat].label} ({categoryCounts[cat]})
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.bulkActionWrapper}>
            <button
              type="button"
              className={`${styles.bulkButton} ${styles.rejectBulkButton}`}
              onClick={() =>
                setExpandedBulkAction(expandedBulkAction === "reject" ? null : "reject")
              }
              title="Reject suggestions"
            >
              <RejectIcon />
              Reject
              <ChevronIcon expanded={expandedBulkAction === "reject"} />
            </button>
            {expandedBulkAction === "reject" && (
              <div className={styles.bulkDropdown}>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => handleBulkAction("reject")}
                >
                  All suggestions ({totalCount})
                </button>
                <div className={styles.dropdownDivider} />
                {ALL_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={styles.dropdownItem}
                    onClick={() => handleBulkAction("reject", cat)}
                    disabled={categoryCounts[cat] === 0}
                  >
                    <span
                      className={styles.categoryDot}
                      style={{ backgroundColor: `var(--color-suggest-${cat})` }}
                    />
                    {CATEGORY_CONFIG[cat].label} ({categoryCounts[cat]})
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <button
          type="button"
          className={`${styles.filterButton} ${activeFilters.length === ALL_CATEGORIES.length ? styles.filterButtonActive : ""}`}
          onClick={toggleAllFilters}
        >
          All
        </button>
        {ALL_CATEGORIES.map((category) => (
          <button
            key={category}
            type="button"
            className={`${styles.filterButton} ${activeFilters.includes(category) ? styles.filterButtonActive : ""}`}
            onClick={() => toggleFilter(category)}
            disabled={categoryCounts[category] === 0}
            style={
              activeFilters.includes(category)
                ? { borderColor: `var(--color-suggest-${category})` }
                : undefined
            }
          >
            <span
              className={styles.categoryDot}
              style={{ backgroundColor: `var(--color-suggest-${category})` }}
            />
            {CATEGORY_CONFIG[category].label}
            <span className={styles.filterCount}>{categoryCounts[category]}</span>
          </button>
        ))}
      </div>

      {onConfidenceFilterChange && (
        <div className={styles.confidenceFilters}>
          <span className={styles.confidenceLabel}>Confidence:</span>
          {(["high", "medium", "low"] as ConfidenceLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              className={`${styles.confidenceFilterButton} ${confidenceFilter === level ? styles.confidenceFilterActive : ""} ${styles[`confidence${level.charAt(0).toUpperCase() + level.slice(1)}`]}`}
              onClick={() => toggleConfidenceFilter(level)}
              disabled={confidenceCounts[level] === 0}
            >
              <ConfidenceIcon level={level} />
              {CONFIDENCE_CONFIG[level].label}
              <span className={styles.filterCount}>{confidenceCounts[level]}</span>
            </button>
          ))}
        </div>
      )}

      {onSortChange && (
        <div className={styles.sortControls}>
          <span className={styles.sortLabel}>Sort:</span>
          <div className={styles.sortWrapper}>
            <button
              type="button"
              className={styles.sortButton}
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              <SortIcon />
              {sortBy === "default"
                ? "Default"
                : sortBy === "confidence-high"
                  ? "Highest confidence"
                  : "Lowest confidence"}
              <ChevronIcon expanded={showSortDropdown} />
            </button>
            {showSortDropdown && (
              <div className={styles.sortDropdown}>
                <button
                  type="button"
                  className={`${styles.dropdownItem} ${sortBy === "default" ? styles.dropdownItemActive : ""}`}
                  onClick={() => handleSortChange("default")}
                >
                  Default
                </button>
                <button
                  type="button"
                  className={`${styles.dropdownItem} ${sortBy === "confidence-high" ? styles.dropdownItemActive : ""}`}
                  onClick={() => handleSortChange("confidence-high")}
                >
                  Highest confidence first
                </button>
                <button
                  type="button"
                  className={`${styles.dropdownItem} ${sortBy === "confidence-low" ? styles.dropdownItemActive : ""}`}
                  onClick={() => handleSortChange("confidence-low")}
                >
                  Lowest confidence first
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.legend}>
        {ALL_CATEGORIES.map((category) => (
          <div key={category} className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: `var(--color-suggest-${category})` }}
            />
            <span className={styles.legendLabel}>{CATEGORY_CONFIG[category].description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AcceptIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.667 3.5L5.25 9.917L2.333 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RejectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: expanded ? "rotate(180deg)" : undefined,
        transition: "transform 0.15s ease",
      }}
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.333 4L6 11.333L2.667 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ConfidenceIcon({ level }: { level: ConfidenceLevel }) {
  const bars = level === "high" ? 3 : level === "medium" ? 2 : 1;
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="1"
        y="8"
        width="2"
        height="3"
        rx="0.5"
        fill={bars >= 1 ? "currentColor" : "var(--color-overlay-heavy)"}
      />
      <rect
        x="5"
        y="5"
        width="2"
        height="6"
        rx="0.5"
        fill={bars >= 2 ? "currentColor" : "var(--color-overlay-heavy)"}
      />
      <rect
        x="9"
        y="2"
        width="2"
        height="9"
        rx="0.5"
        fill={bars >= 3 ? "currentColor" : "var(--color-overlay-heavy)"}
      />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 4L6 1L9 4M3 8L6 11L9 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
